import React, { useEffect, useState } from "react";
import {
  FileCheck2, AlertTriangle, CheckCircle2, Info, Loader2, Sparkles, X,
  Package, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { submissionCheck, submissionPack, errorMessage } from "../../lib/api";

const colorClasses = (color) => ({
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-900",
  red: "border-red-200 bg-red-50 text-red-900",
}[color] || "border-zinc-200 bg-zinc-50 text-zinc-900");

const KIND_META = {
  missing_section: { icon: Info, label: "Missing section", color: "amber" },
  unassigned_sections: { icon: Info, label: "Unassigned", color: "amber" },
  word_count_deviation: { icon: Info, label: "Word count off target", color: "indigo" },
  duplicate_reference: { icon: AlertTriangle, label: "Duplicate reference", color: "amber" },
  ghost_figure_reference: { icon: AlertTriangle, label: "Missing figure/table", color: "amber" },
  possible_duplicate_cover: { icon: Info, label: "Possible duplicate cover", color: "amber" },
  style_variation: { icon: Info, label: "Style variation", color: "indigo" },
};

/** Submission Builder — the "one button = one outcome" panel. Shows a phrase-
 * based Assignment Health status + a "still needed" list, then one primary
 * Generate Submission Pack action that produces a zip. */
export default function SubmissionBuilder({ group, onClose, onGenerated }) {
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await submissionCheck(group.id);
        if (alive) setChecks(res);
      } catch (e) {
        toast.error(errorMessage(e));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [group.id]);

  const generatePack = async () => {
    setGenerating(true);
    try {
      const res = await submissionPack(group.id);
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${group.title || "Group Assignment"} Submission Pack.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Submission Pack ready — the group is now locked.");
      onGenerated?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setGenerating(false); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-6 mt-4 flex items-center gap-3" data-testid="submission-builder-loading">
        <Loader2 className="animate-spin text-indigo-600" size={18} />
        <span className="text-sm text-indigo-900">Checking your assignment…</span>
      </div>
    );
  }
  if (!checks) return null;

  const healthColor =
    checks.sections_missing === 0 && checks.warning_count === 0 ? "emerald" :
    checks.sections_missing === 0 ? "amber" : "indigo";
  const healthStyle = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
  }[healthColor];

  return (
    <div className="rounded-2xl border-2 border-indigo-500 bg-white shadow-lg mt-4 overflow-hidden" data-testid="submission-builder">
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white px-6 py-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} />
            <span className="text-[10px] uppercase tracking-widest font-semibold">Submission Builder</span>
            <span className="text-[10px] bg-white/20 rounded-full px-2 py-0.5">Coming to Ultra Pro</span>
          </div>
          <h3 className="font-display text-xl font-semibold">Generate the Submission Pack</h3>
          <p className="text-sm text-indigo-100 mt-1">
            One button. MyAE runs every check, merges every section, and packages your submission.
          </p>
        </div>
        {onClose && (
          <button onClick={onClose} data-testid="submission-builder-close" className="text-indigo-200 hover:text-white p-1"><X size={18} /></button>
        )}
      </div>

      <div className="p-5">
        {/* Assignment Health headline */}
        <div className={`rounded-xl border px-4 py-3 mb-4 flex items-center gap-3 ${healthStyle}`} data-testid="submission-health">
          {healthColor === "emerald" ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold opacity-70">Assignment Health</p>
            <p className="text-lg font-display font-semibold">{checks.health_phrase}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-[10px] uppercase tracking-widest font-semibold opacity-70">Sections</p>
            <p className="text-lg font-display tabular-nums">{checks.sections_ready} / {checks.sections_total}</p>
          </div>
        </div>

        {/* What's still needed — the FIRST thing the student sees */}
        {checks.missing_list && checks.missing_list.length > 0 && (
          <div className="mb-4" data-testid="submission-missing-list">
            <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <FileCheck2 size={13} /> Still needed
            </p>
            <ul className="space-y-1.5">
              {checks.missing_list.map((m, i) => (
                <li key={i} className="text-sm text-zinc-800 flex items-start gap-2" data-testid={`submission-missing-${i}`}>
                  <span className="text-amber-500 mt-0.5">•</span> {m}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* All other notes (collapsible detail) */}
        {checks.warnings.length > 0 && (
          <details className="mb-4" data-testid="submission-warnings-details">
            <summary className="cursor-pointer text-xs font-semibold text-zinc-600 hover:text-zinc-900">
              Show all {checks.warning_count} note{checks.warning_count === 1 ? "" : "s"}
            </summary>
            <div className="mt-2 space-y-2">
              {checks.warnings.map((w, i) => {
                const meta = KIND_META[w.kind] || { icon: Info, label: "Note", color: "zinc" };
                const Icon = meta.icon;
                return (
                  <div
                    key={i}
                    className={`rounded-lg border px-3 py-2 flex items-start gap-2 text-sm ${colorClasses(meta.color)}`}
                    data-testid={`submission-warning-${w.kind}`}
                  >
                    <Icon size={14} className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-70 mb-0.5">{meta.label}</p>
                      <p className="leading-snug">{w.message}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}

        {/* Primary action */}
        <button
          onClick={generatePack}
          disabled={generating || group.locked}
          data-testid="submission-generate-pack"
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-base px-4 py-3 shadow-sm disabled:opacity-50 transition-colors"
        >
          {generating ? <Loader2 size={18} className="animate-spin" /> : <Package size={18} />}
          {generating
            ? "Preparing your Submission Pack…"
            : group.locked
              ? "Group already locked — unlock to regenerate"
              : "Generate Submission Pack"}
        </button>
        <p className="text-[11px] text-zinc-500 mt-2 text-center flex items-center justify-center gap-1">
          <Lock size={10} /> The group will be locked after generation. Owner can unlock from the Manage menu.
        </p>
      </div>
    </div>
  );
}
